import type { CoreLogger } from '@elara/core/logger';
import type { ClientPlatform } from '@elara/domain/client-platform';

import type { FlowHttpError } from '#lib/flow-utils.js';
import type { AuthContainer } from './FirebaseAuthContainer.js';
import type { FirestoreContainer } from './FirestoreContainer.js';

import { createContainer } from '@elara/core/di';

import { getEnv } from '#env-schema.js';
import { createAppFlowsContainer, createDebugFlowsContainer } from '#flows.js';
import { setHotelPhotoEnricher } from '#lib/enrich-hotel-photos.js';
import { setMediaBaseUrl } from '#lib/media-url.js';

import { createChatsContainer } from './ChatsContainer.js';
import { createDestinationsContainer } from './DestinationsContainer.js';
import { createFlightOptionsContainer } from './FlightOptionsContainer.js';
import { createHotelsContainer } from './HotelsContainer.js';
import { createItineraryContainer } from './ItineraryContainer.js';
import { createNotificationsContainer } from './NotificationsContainer.js';
import { createPaymentMethodsContainer } from './PaymentMethodsContainer.js';
import { createPlacesContainer } from './PlacesContainer.js';
import { createRoomOptionsContainer } from './RoomOptionsContainer.js';
import { createShortlistContainer } from './ShortlistContainer.js';
import { createStoriesContainer } from './StoriesContainer.js';
import { createTripsContainer } from './TripsContainer.js';
import { createUserProfileContainer } from './UserProfileContainer.js';

/**
 * Platform ports injected at composition time (ELARA-717).
 *
 * Web composes the container with no overrides (every port falls back to its web default).
 * React Native injects platform-specific implementations here — e.g. a streaming-capable
 * `fetchImpl` (`expo/fetch`, ELARA-688) so SSE token-streaming works on-device.
 */
export interface FeaturesContainerPorts {
  fetchImpl?: typeof fetch;
  /**
   * Which app shell this container serves. Sent as a header on every flow request so the server
   * can record which apps a member has signed in from (surfaced in the admin user list). Defaults
   * to `web`; the Expo app injects `Platform.OS`.
   */
  clientPlatform?: ClientPlatform;
  /**
   * Whether the device reads as online, so a failed flow request is reported as "offline" only when
   * it truly is (ELARA-1146). The Expo app injects its NetInfo-backed connectivity port; web leaves
   * it unset and the transport falls back to `navigator.onLine`.
   */
  isOnline?: () => boolean;
}

export function createFeaturesContainer(
  auth: AuthContainer,
  firestore: FirestoreContainer,
  coreLogger: CoreLogger,
  ports: FeaturesContainerPorts = {},
) {
  const getAuthToken = async (): Promise<string | undefined> => {
    const tokenResult = await auth.getIdTokenResult();
    return tokenResult?.token;
  };
  const { fetchImpl, clientPlatform = 'web', isOnline } = ports;
  const flowApiBaseUrl = getEnv().VITE_FLOW_API_BASE_URL?.trim() || undefined;

  // Stored media-proxy paths (/api/media/...) resolve against the same host as the flow API:
  // unset (web same-origin) leaves them relative; native/preview shells get absolute URLs.
  setMediaBaseUrl(flowApiBaseUrl);
  let signOutInFlight: Promise<void> | undefined;

  const handleUnauthenticatedFlowError = (error: FlowHttpError): void => {
    const currentUser = auth.getCurrentUser();
    if (!currentUser) {
      return;
    }
    if (signOutInFlight) {
      return;
    }

    signOutInFlight = auth
      .signOut()
      .then(() => {
        coreLogger.warn(
          {
            uid: currentUser.uid,
            status: error.status,
            code: error.code,
            errorMessage: error.message,
          },
          'Flow request returned unauthenticated while a user session existed; signed out client user',
        );
      })
      .catch((signOutError: unknown) => {
        coreLogger.warn(
          {
            uid: currentUser.uid,
            status: error.status,
            code: error.code,
            errorMessage: error.message,
            signOutError,
          },
          'Failed to sign out client user after unauthenticated flow response',
        );
      })
      .finally(() => {
        signOutInFlight = undefined;
      });
  };

  // Type assertion needed to avoid "type exceeds maximum length" error with deeply nested containers
  const $features = createContainer('features')
    .nested('flows', () =>
      createAppFlowsContainer({
        getAuthToken,
        baseUrl: flowApiBaseUrl,
        onUnauthenticatedError: handleUnauthenticatedFlowError,
        fetchImpl,
        clientPlatform,
        isOnline,
      }),
    )
    .nested('debugFlows', () =>
      createDebugFlowsContainer({
        getAuthToken,
        baseUrl: flowApiBaseUrl,
        onUnauthenticatedError: handleUnauthenticatedFlowError,
        fetchImpl,
        clientPlatform,
        isOnline,
      }),
    )
    .nested('destinations', () => createDestinationsContainer(firestore))
    .nested('flightOptions', () => createFlightOptionsContainer(firestore))
    .nested('hotels', () => createHotelsContainer(firestore))
    .nested('places', () => createPlacesContainer(firestore))
    .nested('stories', () => createStoriesContainer(firestore))
    .nested('roomOptions', () => createRoomOptionsContainer(firestore))
    .nested('itinerary', () => createItineraryContainer(firestore))
    .nested('trips', ['flows', 'destinations'], ({ flows, destinations }) =>
      createTripsContainer(flows, firestore, destinations),
    )
    .nested('chats', ['flows'], ({ flows }) => createChatsContainer(flows, firestore, coreLogger))
    .nested('shortlist', ['flows'], ({ flows }) => createShortlistContainer(flows, firestore))
    .nested('userProfile', ['flows'], ({ flows }) => createUserProfileContainer(flows, firestore))
    .nested('notifications', ['flows'], ({ flows }) => createNotificationsContainer(flows, firestore))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .nested('wallet', ['flows'], ({ flows }) => createPaymentMethodsContainer(flows, firestore)) as any;

  // Hotel photo backfill port: HotelModel.allImages fires this from its getter, covering every
  // hotel render surface on web AND mobile. Resolved lazily so the flow transport only
  // materializes when a hotel actually needs enrichment.
  setHotelPhotoEnricher((input: { hotelId: string }) =>
    ($features.use as { flows: { enrichHotelPhotos: (i: { hotelId: string }) => Promise<unknown> } }).flows.enrichHotelPhotos(input),
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return $features;
}

export type $FeaturesContainer = ReturnType<typeof createFeaturesContainer>;
export type FeaturesContainer = $FeaturesContainer['use'];
